import type { dwrClassesById } from '@/data/classes';
import type { Character } from '@/schemas/character';
import { hasMoveSetup, type Move } from '@/schemas/move';
import { getStatCapForLevelUp } from '@/services/levelUp';
import { isMoveSetupComplete } from '../../services/moveSetupComplete';
import {
  isArchetypeStepComplete,
  isConfirmStepComplete,
  isGrantedStepComplete,
  isMulticlassStepComplete,
  isPastTenStepComplete,
  isSpellsStepComplete,
} from './levelUpCompleteness';
import {
  pickArchetypeBackStep,
  pickConfirmPrevStep,
  pickIntroNextStep,
  pickMoveBackStep,
  pickNextStep,
  pickSpellsBackStep,
  type StepFlowFlags,
} from './stepRouting';
import type { LevelUpDraft, LevelUpStepId } from './types';

/**
 * Pure helpers per pilotare lo `<StepNav>` esterno renderizzato da
 * `LevelUpFlowPage` (PR 8 esteso PR 9). Centralizzati qui per:
 *  - Riuso testabile delle pure functions
 *  - Mantenere `LevelUpFlowPage.tsx` sotto `max-lines: 400`
 *  - Evitare violazione `react-refresh/only-export-components`
 *
 * PR 9 estende lo shell nav a 7 step (intro/stat/move + pastTen/granted/
 * multiclass/archetype). Spells + confirm restano fuori (PR 10).
 */

type ClassDef = NonNullable<(typeof dwrClassesById)[keyof typeof dwrClassesById]>;

/**
 * **PR 10**: tutti i 9 step level-up usano lo `<StepNav>` esterno.
 * Lo step `confirm` ha `isLast=true` → button "Conferma e applica L{N}"
 * (decisione E1 PR 10).
 */
export function isShellNavStep(_step: LevelUpStepId): boolean {
  return true;
}

/**
 * Lookup `step → predicate(...)`. Funzioni pure di completion check, una
 * per step. Centralizza i 7 predicati in tabella per evitare il classico
 * `if/else` con complexity > 10.
 */
const STEP_COMPLETE_PREDICATES: Record<LevelUpStepId, (ctx: StepCompletionContext) => boolean> = {
  intro: () => true,
  pastTen: ({ draft }) => isPastTenStepComplete(draft),
  stat: ({ character, draft }) => isStatStepCompleteImpl(character, draft),
  move: ({ character, draft, moveDef }) => isMoveStepCompleteImpl(character, draft, moveDef),
  granted: ({ character, classDef, draft, moveDef }) =>
    moveDef !== undefined &&
    isGrantedStepComplete(draft.grantedChoice, character, classDef, moveDef),
  multiclass: ({ character, classDef, draft, moveDef }) =>
    moveDef !== undefined &&
    isMulticlassStepComplete(draft.multiclassPick, character, classDef, moveDef),
  archetype: ({ character, classDef, draft }) =>
    isArchetypeStepComplete(draft, character, classDef),
  spells: ({ character, draft }) => isSpellsStepComplete(draft, character),
  confirm: ({ character, draft }) => isConfirmStepComplete(draft, character),
};

interface StepCompletionContext {
  character: Character;
  classDef: ClassDef;
  draft: LevelUpDraft;
  moveDef: Move | undefined;
}

export function isStepComplete(
  step: LevelUpStepId,
  character: Character,
  classDef: ClassDef,
  draft: LevelUpDraft,
  moveDef: Move | undefined,
): boolean {
  return STEP_COMPLETE_PREDICATES[step]({ character, classDef, draft, moveDef });
}

function isStatStepCompleteImpl(character: Character, draft: LevelUpDraft): boolean {
  if (!draft.statIncreased) return false;
  const cap = getStatCapForLevelUp(character);
  return !cap[draft.statIncreased];
}

function isMoveStepCompleteImpl(
  character: Character,
  draft: LevelUpDraft,
  moveDef: Move | undefined,
): boolean {
  if (!draft.moveAdded || !moveDef) return false;
  if (!hasMoveSetup(moveDef)) return true;
  return isMoveSetupComplete(moveDef, draft.moveAdded, character.archetypeId);
}

const PREV_STEP_RESOLVERS: Record<LevelUpStepId, (flags: StepFlowFlags) => LevelUpStepId> = {
  intro: () => 'intro',
  pastTen: () => 'intro',
  stat: () => 'intro',
  move: (flags) => pickMoveBackStep(flags),
  granted: () => 'move',
  multiclass: (flags) => (flags.needsGrantedStep ? 'granted' : 'move'),
  archetype: (flags) => pickArchetypeBackStep(flags),
  spells: (flags) => pickSpellsBackStep(flags),
  confirm: (flags) => pickConfirmPrevStep(flags),
};

export function prevForShellStep(step: LevelUpStepId, flags: StepFlowFlags): LevelUpStepId {
  return PREV_STEP_RESOLVERS[step](flags);
}

export function nextForShellStep(
  step: LevelUpStepId,
  character: Character,
  draft: LevelUpDraft,
  flags: StepFlowFlags,
): LevelUpStepId {
  if (step === 'intro') return pickIntroNextStep(character, flags);
  // D10 / FI-11: dopo pastTen, Addio salta direttamente al confirm;
  // Apprendista/Instancabile prosegue al move (stat skippato post-10°).
  if (step === 'pastTen') return draft.pastTen === 'addio' ? 'confirm' : 'move';
  if (step === 'stat') return 'move';
  if (step === 'move') return pickNextStep('move', flags);
  if (step === 'granted') return pickNextStep('granted', flags);
  if (step === 'multiclass') return pickNextStep('multiclass', flags);
  if (step === 'archetype') return pickNextStep('archetype', flags);
  if (step === 'spells') return 'confirm';
  // confirm: il "next" è handleApply (azione finale), non un setStep.
  // Il caller (LevelUpFlowPage) intercetta step==='confirm' e chiama onApply.
  return step;
}
